﻿import { Component, Property } from "@wonderlandengine/api";
import { ensureInteractable } from "./interactable.js";

/**
 * 虚拟键盘组件 - 德语 QWERTZ 适配版
 *
 * 特点：
 * 1. 保留稳定版键盘布局
 * 2. 每个按键独立 hitbox，不再依赖大 Collider 的 cursorPos 判断
 * 3. mesh、text、hitbox 分离
 * 4. 点击区域略大于视觉按键，提升点击容错
 * 5. 空键不输入 undefined
 * 6. Enter 默认提交并隐藏，不额外插入换行
 * 7. hide() 不缩放键盘根 object，只隐藏键盘内部按键
 * 8. show() 可由外部碰撞点重新显示键盘
 */
export class Keyboard extends Component {
  static TypeName = "keyboard";

  static Properties = {
    panelSizeX: Property.float(0.008),
    panelSizeY: Property.float(0.008),
    padding: Property.int(2),
    keyboardScale: Property.float(1.0),

    keyMesh: Property.mesh(),
    keyMaterial: Property.material(),
    textMaterial: Property.material(),
    textSize: Property.float(1.2),
    clickMaterial: Property.material(),
  };

  init() {
    this.textChangedCallbacks = [];
    this.submitCallbacks = [];

    this.text = "";
    this.shift = false;
    this.keyboardIndex = 0;

    this.config = {};
    this.content = {};

    this._isVisible = true;
  }

  start() {
    this.text = "";
    this.shift = false;

    this.getConfig();

    const keys = Object.entries(this.config);
    const keyboardScale = Math.max(0.5, this.keyboardScale || 1.0);
    const panelSizeX = this.panelSizeX * keyboardScale;
    const panelSizeY = this.panelSizeY * keyboardScale;
    const textSize = this.textSize * keyboardScale;

    const children = this.engine.scene.addObjects(
      keys.length,
      this.object,
      keys.length,
    );

    for (let i = 0; i < keys.length; i++) {
      const currentChild = children[i];
      const keyName = keys[i][0];
      const data = keys[i][1];

      this.config[keyName].object = currentChild;

      /**
       * 使用中心点定位。
       * 这是稳定版布局的关键。
       */
      const keyWorldW = data.width * panelSizeX;
      const keyWorldH = data.height * panelSizeY;

      const centerX = (data.x + data.width / 2) * panelSizeX;
      const centerY = (data.y - data.height / 2) * panelSizeY;

      currentChild.setPositionLocal([centerX, centerY, 0.02]);
      currentChild.setScalingLocal([1, 1, 1]);

      /**
       * 视觉尺寸。
       * 稍微小于逻辑按键尺寸，保留按键之间的间隙。
       */
      const visualW = keyWorldW * 0.82;
      const visualH = keyWorldH * 0.72;

      /**
       * 视觉按键 mesh
       */
      const meshChild = this.engine.scene.addObject(currentChild);
      meshChild.setPositionLocal([0, 0, 0]);
      meshChild.setScalingLocal([visualW, visualH, 0.12]);

      const mesh = meshChild.addComponent("mesh");

      if (this.keyMesh) mesh.mesh = this.keyMesh;
      if (this.keyMaterial) mesh.material = this.keyMaterial;

      this.config[keyName].mesh = mesh;
      this.config[keyName].meshChild = meshChild;
      this.config[keyName].meshScale = [visualW, visualH, 0.12];

      /**
       * 独立点击 hitbox
       *
       * hitChild 不缩放。
       * collision.extents 直接使用按键世界尺寸。
       * 这样不会出现 scale 和 extents 二次叠加导致点击区域过小的问题。
       */
      const hitChild = this.engine.scene.addObject(currentChild);
      hitChild.setPositionLocal([0, 0, 0]);
      hitChild.setScalingLocal([1, 1, 1]);

      const { collision, target: cursorTarget } = ensureInteractable(hitChild, {
        extents: [
          keyWorldW * 0.48,
          keyWorldH * 0.48,
          0.05,
        ],
      });
      cursorTarget.onDown.add(() => this.onSelect(keyName));

      this.config[keyName].hitChild = hitChild;
      this.config[keyName].hitScale = [1, 1, 1];
      this.config[keyName].collision = collision;
      this.config[keyName].cursorTarget = cursorTarget;

      /**
       * 文字
       */
      const textChild = this.engine.scene.addObject(currentChild);
      textChild.setPositionLocal([0, 0, 0.03]);
      textChild.setScalingLocal([textSize, textSize, textSize]);

      const textComp = textChild.addComponent("text");

      if (this.textMaterial) textComp.material = this.textMaterial;

      textComp.alignment = "center";
      textComp.justification = "middle";

      this.config[keyName].textChild = textChild;
      this.config[keyName].textScale = [textSize, textSize, textSize];
      this.config[keyName].textComponent = textComp;
    }

    this.getContent(0);
  }

  onSelect(key) {
    /**
     * 键盘隐藏后，即使隐藏区域仍然触发 CursorTarget，
     * 也不允许继续输入字符。
     */
    if (!this._isVisible) return;

    if (!this.config[key]) return;

    let change = null;

    switch (key) {
      case "btn34":
        /**
         * Enter：提交并隐藏。
         * 默认不向文本中加入换行。
         */
        this.notifySubmitted();
        this.hide();
        break;

      case "btn32":
        change = " ";
        break;

      case "btn30":
        /**
         * ?123 / abc
         */
        this.shift = false;
        this.getContent(this.keyboardIndex < 2 ? 2 : 0);
        break;

      case "btn28":
        /**
         * Del
         */
        change = -1;
        break;

      case "btn20":
        /**
         * Shift
         */
        this.shift = !this.shift;

        this.getContent(
          this.shift
            ? this.keyboardIndex < 2
              ? 1
              : 3
            : this.keyboardIndex < 2
              ? 0
              : 2,
        );
        break;

      default:
        if (!(key in this.content)) return;
        change = this.content[key];
        break;
    }

    this.flashKey(key);

    if (change !== null) {
      if (change === -1) {
        this.text = this.text.slice(0, -1);
      } else {
        this.text += change;
      }

      this.notifyTextChanged();
    }
  }

  flashKey(key) {
    if (!this._isVisible) return;
    if (!this.clickMaterial) return;
    if (!this.config[key]) return;
    if (!this.config[key].mesh) return;

    const mesh = this.config[key].mesh;
    const originalMat = mesh.material;

    mesh.material = this.clickMaterial;

    setTimeout(() => {
      if (mesh.material === this.clickMaterial) {
        mesh.material = originalMat;
      }
    }, 150);
  }

  getConfig() {
    const width = 51.2;
    const height = 40;

    let x = 2;
    let y = 2;

    this.config = {};

    /**
     * 第一行：qwertzuiop
     */
    x = 2;
    y = 2;

    for (let i = 0; i < 10; i++) {
      this.config["btn" + i] = {
        x,
        y,
        width,
        height,
      };
      x += width;
    }

    /**
     * 第二行：asdfghjklö
     */
    x = 2;
    y -= 50;

    for (let i = 0; i < 10; i++) {
      this.config["btn" + (i + 10)] = {
        x,
        y,
        width,
        height,
      };
      x += width;
    }

    /**
     * 第三行：Shift y x c v b n m Del
     */
    x = 2;
    y -= 50;

    for (let i = 0; i < 9; i++) {
      const w = i === 0 || i === 8 ? width * 1.5 : width;

      this.config["btn" + (i + 20)] = {
        x,
        y,
        width: w,
        height,
      };

      x += w;
    }

    /**
     * 第四行：?123 ä space ü Enter
     */
    x = 2;
    y -= 50;

    for (let i = 0; i < 5; i++) {
      const w = i === 0 || i === 4 ? width * 2 : i === 2 ? width * 4 : width;

      this.config["btn" + (i + 30)] = {
        x,
        y,
        width: w,
        height,
      };

      x += w;
    }

    for (const key in this.config) {
      this.config[key].position = {
        x: this.config[key].x,
        y: this.config[key].y,
      };
    }
  }

  getContent(idx) {
    this.content = {};
    this.keyboardIndex = idx || 0;

    const layouts = [
      /**
       * 0: 德语小写 QWERTZ
       */
      [
        "q",
        "w",
        "e",
        "r",
        "t",
        "z",
        "u",
        "i",
        "o",
        "p",

        "a",
        "s",
        "d",
        "f",
        "g",
        "h",
        "j",
        "k",
        "l",
        "ö",

        "Shift",
        "y",
        "x",
        "c",
        "v",
        "b",
        "n",
        "m",
        "Del",

        "",
        "?123",
        "ä",
        "  ",
        "ü",
        "Enter",
      ],

      /**
       * 1: 德语大写 QWERTZ
       */
      [
        "Q",
        "W",
        "E",
        "R",
        "T",
        "Z",
        "U",
        "I",
        "O",
        "P",

        "A",
        "S",
        "D",
        "F",
        "G",
        "H",
        "J",
        "K",
        "L",
        "Ö",

        "Shift",
        "Y",
        "X",
        "C",
        "V",
        "B",
        "N",
        "M",
        "Del",

        "",
        "?123",
        "Ä",
        "  ",
        "Ü",
        "Enter",
      ],

      /**
       * 2: 符号页 1
       */
      [
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "0",

        "ß",
        "@",
        "#",
        "%",
        "&",
        "*",
        "/",
        "-",
        "+",
        "(",

        "Shift",
        ")",
        "?",
        "!",
        '"',
        "'",
        "\\",
        ":",
        "Del",

        "",
        "abc",
        ",",
        "  ",
        ".",
        "Enter",
      ],

      /**
       * 3: 符号页 2
       */
      [
        "1",
        "2",
        "3",
        "4",
        "5",
        "6",
        "7",
        "8",
        "9",
        "0",

        "€",
        "£",
        "$",
        "^",
        "=",
        "|",
        "{",
        "}",
        "[",
        "]",

        "Shift",
        "<",
        ">",
        "_",
        "`",
        "~",
        ":",
        ";",
        "Del",

        "",
        "abc",
        ",",
        "  ",
        ".",
        "Enter",
      ],
    ];

    const keys = layouts[this.keyboardIndex];

    for (let i = 0; i < keys.length; i++) {
      const keyName = "btn" + i;
      const label = keys[i];

      if (!this.config[keyName]) continue;

      /**
       * 这里空键也要写入 textComponent，
       * 防止切换布局后旧字符残留。
       */
      if (this.config[keyName].textComponent) {
        this.config[keyName].textComponent.text = label;
      }

      if (label !== "") {
        this.content[keyName] = label;
      }
    }
  }

  setKeyboardVisible(visible) {
    this._isVisible = visible;

    for (const key in this.config) {
      const data = this.config[key];

      /**
       * 不缩放 this.object 根节点。
       * 只隐藏键盘内部对象。
       */
      if (data.object) {
        data.object.setScalingLocal(visible ? [1, 1, 1] : [0, 0, 0]);
      }

      /**
       * 尽量关闭键盘按键自己的交互。
       * 有些 Wonderland Engine 版本中 active 行为可能不完全可靠，
       * 所以 onSelect() 里的 _isVisible 判断仍然保留。
       */
      if (data.collision) {
        data.collision.active = visible;
      }

      if (data.cursorTarget) {
        data.cursorTarget.active = visible;
      }

      /**
       * 额外保险：隐藏时把 hitbox 也缩小。
       */
      if (data.hitChild) {
        data.hitChild.setScalingLocal(visible ? data.hitScale : [0, 0, 0]);
      }
    }
  }

  show() {
    this.setKeyboardVisible(true);
  }

  hide() {
    this.setKeyboardVisible(false);
  }

  isVisible() {
    return this._isVisible;
  }

  addTextChangedCallback(f) {
    if (typeof f === "function") {
      this.textChangedCallbacks.push(f);
    }
  }

  addSubmitCallback(f) {
    if (typeof f === "function") {
      this.submitCallbacks.push(f);
    }
  }

  notifyTextChanged() {
    for (const f of this.textChangedCallbacks) {
      f(this.text);
    }
  }

  notifySubmitted() {
    for (const f of this.submitCallbacks) {
      f(this.text);
    }
  }

  getText() {
    return this.text;
  }

  setText(text) {
    this.text = text ?? "";
    this.notifyTextChanged();
  }
}
